import { Suspense, lazy, useMemo, useState } from 'react';
import type { DayPickerLocale, Formatters, Labels, Modifiers } from 'react-day-picker';

import Loading, { LoadingBall } from '@ingka/loading';
import type { TranslateFunction } from '@repo/phrase';

import './daypicker.styles.scss';

const ReactDayPicker = lazy(() => import('react-day-picker').then(({ DayPicker }) => ({ default: DayPicker })));

interface DayPickerProps {
    initialDate?: Date;
    weekNumbers?: boolean;
    animated?: boolean;
    translate: TranslateFunction;
    onDayChange?: (date: Date) => void;
    onMonthChange?: (date: Date) => void;
    availableDates?: Date[];
    currentDate?: Date;
    startMonth?: Date;
    labels?: Partial<Labels>;
    formatters?: Partial<Formatters>;
    locale?: DayPickerLocale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    timeZone?: string | undefined;
    disabledDates?: [{ before: Date }, { after: Date }];
}

/**
 * DayPicker component for selecting dates.
 * @param {DayPickerProps} props - The DayPicker component props.
 * @param {Date} [props.initialDate] - Date selected when picker is rendered. Optional, default "today".
 * @param {boolean} [props.weekNumbers] - Displays week numbers column.
 * @param {boolean} [props.animated] - Transition animation when navigating months.
 * @param {TranslateFunction} [props.translate] - Translations.
 * @param {Function} [props.onDayChange] - Callback function triggered when selected date changes.
 * @param {Date[]} [props.availableDates] - Array of dates to mark as available with a green dot.
 * @param { before: Date; after: Date } [props.disabledDates] - Object specifying a date range to disable.
 * @returns {JSX.Element} Rendered DayPicker component.
 */

const isSameMonth = (left: Date, right: Date): boolean => {
    return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
};

const isSameDay = (left: Date, right: Date): boolean => {
    return (
        left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate()
    );
};

const modifiersClassNames = {
    available: 'rdp-day--available',
    current: 'rdp-day--current',
};

export const DayPicker = (props: DayPickerProps) => {
    const {
        onDayChange,
        onMonthChange,
        initialDate,
        translate,
        availableDates,
        currentDate,
        startMonth,
        labels: labelsProp,
        formatters: formattersProp,
        locale,
        weekStartsOn,
        disabledDates,
    } = props;
    const fallbackDate = useMemo(() => new Date(), []);
    const resolvedInitialDate = initialDate ?? fallbackDate;

    const [selectedDate, setSelectedDate] = useState<Date>(resolvedInitialDate);
    const [selectedMonth, setSelectedMonth] = useState<Date>(resolvedInitialDate);
    const [prevInitialDate, setPrevInitialDate] = useState<Date | undefined>(initialDate);

    // Sync internal state when initialDate prop changes (React recommended pattern)
    // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    if (initialDate && prevInitialDate && !isSameDay(prevInitialDate, initialDate)) {
        setPrevInitialDate(initialDate);
        setSelectedDate(initialDate);
        if (!isSameMonth(selectedMonth, initialDate)) {
            setSelectedMonth(initialDate);
        }
    }

    const handleMonthChange = (date: Date) => {
        setSelectedMonth(date);
        onMonthChange?.(date);
    };

    const handleSelect = (date: Date | undefined) => {
        if (!date) {
            return;
        }

        setSelectedDate(date);
        onDayChange?.(isSameDay(date, selectedDate) ? selectedDate : date);

        if (!isSameMonth(selectedMonth, date)) {
            setSelectedMonth(date);
            onMonthChange?.(date);
        }
    };

    const availableDatesForModifiers = useMemo(() => {
        if (!availableDates || availableDates.length === 0) {
            return;
        }
        if (!currentDate) {
            return availableDates;
        }

        const filteredAvailableDates = availableDates.filter((date) => !isSameDay(date, currentDate));
        return filteredAvailableDates.length > 0 ? filteredAvailableDates : undefined;
    }, [availableDates, currentDate]);

    const modifiers = {
        ...(availableDatesForModifiers && { available: availableDatesForModifiers }),
        ...(currentDate && { current: currentDate }),
    };

    const translateMonth = (date: Date): string => {
        const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        return translate(`datepicker_month_${months[date.getMonth()]}`);
    };

    const translateWeekday = (date: Date): string => {
        const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        return translate(`datepicker_weekday_abbr_${days[date.getDay()]}`);
    };

    const buildDayLabel = (date: Date, modifiers?: Modifiers): string => {
        const labelParts = [date.toLocaleDateString()];

        if (modifiers?.today) {
            labelParts.push(translate('datepicker_label_day_button_today'));
        }

        if (modifiers?.selected) {
            labelParts.push(translate('datepicker_label_day_button_selected'));
        }

        return labelParts.join(', ');
    };

    const resolvedLabels: Partial<Labels> = {
        labelDayButton: (date: Date, modifiers: Modifiers) => buildDayLabel(date, modifiers),
        labelGrid: () => translate('datepicker_label_grid'),
        labelGridcell: (date: Date, modifiers?: Modifiers) => buildDayLabel(date, modifiers),
        labelMonthDropdown: () => translate('datepicker_label_month_dropdown'),
        labelNav: () => translate('datepicker_label_nav'),
        labelNext: () => translate('datepicker_label_next_month'),
        labelPrevious: () => translate('datepicker_label_previous_month'),
        labelWeekday: (date: Date) => translateWeekday(date),
        labelWeekNumber: (weekNumber: number) =>
            translate('datepicker_label_week_number_cell', { weekNumber: weekNumber.toString() }),
        labelWeekNumberHeader: () => translate('datepicker_label_week_number'),
        labelYearDropdown: () => translate('datepicker_label_year_dropdown'),
        ...labelsProp,
    };

    const resolvedFormatters: Partial<Formatters> = {
        formatCaption: (date: Date) => translateMonth(date),
        formatWeekdayName: (date: Date) => translateWeekday(date),
        ...formattersProp,
    };

    return (
        <div className="salja-daypicker">
            <Suspense
                fallback={
                    <Loading>
                        <LoadingBall color="primary" />
                    </Loading>
                }
            >
                <ReactDayPicker
                    data-testid="daypicker"
                    required
                    navLayout="around"
                    mode="single"
                    disabled={disabledDates}
                    animate={props.animated ?? false}
                    showWeekNumber={props.weekNumbers ?? false}
                    month={selectedMonth}
                    selected={selectedDate}
                    onSelect={handleSelect}
                    onMonthChange={handleMonthChange}
                    startMonth={startMonth}
                    modifiers={modifiers}
                    modifiersClassNames={modifiersClassNames}
                    locale={locale}
                    weekStartsOn={weekStartsOn}
                    formatters={resolvedFormatters}
                    labels={resolvedLabels}
                    timeZone={props.timeZone ?? 'UTC'}
                    showOutsideDays
                />
            </Suspense>
        </div>
    );
};
